import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { ReactNode, useState } from "react";

interface FilterSectionProps {
  /** Section title */
  title: string;

  /** Content to render inside the section */
  children: ReactNode;

  /** Whether the section is expanded by default */
  defaultExpanded?: boolean;

  /** Number of active filters in this section (shows badge) */
  activeCount?: number;

  /** Optional icon to show before title */
  icon?: ReactNode;

  /** Whether this section can be collapsed */
  collapsible?: boolean;

  /** Callback when expanded state changes */
  onExpandedChange?: (expanded: boolean) => void;

  /** Optional ID for accessibility */
  id?: string;
}

/**
 * Collapsible filter section with Amazon-style design
 *
 * Features:
 * - Smooth expand/collapse animation
 * - Badge showing active filter count
 * - Subtle hover effects
 * - Consistent styling
 */
const FilterSection: React.FC<FilterSectionProps> = ({
  title,
  children,
  defaultExpanded = true,
  activeCount = 0,
  icon,
  collapsible = true,
  onExpandedChange,
  id,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    if (!collapsible) return;
    setExpanded(isExpanded);
    onExpandedChange?.(isExpanded);
  };

  // Non-collapsible variant
  if (!collapsible) {
    return (
      <Box
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 1.5,
          px: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1.5,
          }}
        >
          {icon && (
            <Box
              sx={{
                display: "flex",
                color: theme.palette.text.secondary,
                "& svg": { fontSize: "1.1rem" },
              }}
            >
              {icon}
            </Box>
          )}
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: "0.8125rem",
              color: theme.palette.text.primary,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {title}
          </Typography>
          {activeCount > 0 && (
            <Badge
              badgeContent={activeCount}
              color="primary"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.65rem",
                  height: 16,
                  minWidth: 16,
                },
              }}
            />
          )}
        </Box>
        <Box>{children}</Box>
      </Box>
    );
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      disableGutters
      elevation={0}
      id={id}
      sx={{
        backgroundColor: "transparent",
        "&:before": { display: "none" },
        borderBottom: `1px solid ${theme.palette.divider}`,
        "&.Mui-expanded": {
          margin: 0,
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMore
            sx={{
              fontSize: "1.25rem",
              color: theme.palette.text.secondary,
              transition: "transform 0.2s",
            }}
          />
        }
        aria-controls={id ? `${id}-content` : undefined}
        sx={{
          minHeight: 44,
          px: 0,
          "&.Mui-expanded": {
            minHeight: 44,
          },
          "& .MuiAccordionSummary-content": {
            margin: "10px 0",
            alignItems: "center",
            gap: 1,
            "&.Mui-expanded": {
              margin: "10px 0",
            },
          },
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
          },
        }}
      >
        {icon && (
          <Box
            sx={{
              display: "flex",
              color: expanded
                ? theme.palette.primary.main
                : theme.palette.text.secondary,
              transition: "color 0.2s",
              "& svg": { fontSize: "1.1rem" },
            }}
          >
            {icon}
          </Box>
        )}
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            fontSize: "0.8125rem",
            color: expanded
              ? theme.palette.text.primary
              : theme.palette.text.secondary,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            transition: "color 0.2s",
            flex: 1,
          }}
        >
          {title}
        </Typography>
        {activeCount > 0 && (
          <Badge
            badgeContent={activeCount}
            color="primary"
            sx={{
              mr: 1,
              "& .MuiBadge-badge": {
                fontSize: "0.65rem",
                height: 16,
                minWidth: 16,
              },
            }}
          />
        )}
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: 0,
          pt: 0,
          pb: 2,
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

export default FilterSection;
